import { useState, useEffect } from "react"
import { api } from "aldoo-ra/API"
import Typography from "aldoo-ra/Typography"
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts"

const cmsAPI = api("cms")

// Type color palette — consistent across all charts
const TYPE_COLORS = {
  image:    "#f97316", // orange
  video:    "#6366f1", // indigo
  audio:    "#10b981", // emerald
  document: "#f43f5e", // rose
  text:     "#06b6d4", // cyan
  other:    "#8b5cf6", // violet
}

const TYPE_LABELS = {
  image: "Images", video: "Videos", audio: "Audio",
  document: "Documents", text: "Text", other: "Other",
}

function formatSize(bytes) {
  if (!bytes) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  let size = bytes
  let i = 0
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++ }
  return `${size.toFixed(2)} ${units[i]}`
}

const CustomTooltip = ({ active, payload, label, mode }) => {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="bg-admin_paper border border-admin_bordered rounded-lg p-3 text-sm shadow-lg">
      <p className="font-semibold text-admin_text mb-1">{TYPE_LABELS[d.type || d.ext] || d.type || d.ext}</p>
      {mode === "size" && <p className="text-admin_text_secondary">{d.formattedSize}</p>}
      {mode === "count" && <p className="text-admin_text_secondary">{d.count} {d.count === 1 ? "file" : "files"}</p>}
    </div>
  )
}

const BarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="bg-admin_paper border border-admin_bordered rounded-lg p-3 text-sm shadow-lg">
      <p className="font-semibold text-admin_text mb-1">.{d.ext}</p>
      <p className="text-admin_text_secondary">{d.formattedSize}</p>
      <p className="text-admin_text_secondary">{d.count} {d.count === 1 ? "file" : "files"}</p>
    </div>
  )
}



export default function MediaLibraryStats() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    cmsAPI({ file_stats: {} })
      .then((res) => {
        if (res?.error) { setError(res.error); return }
        setData(res)
      })
      .catch(() => setError("Failed to load stats"))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-admin_text_secondary">
      Loading stats…
    </div>
  )

  if (error) return (
    <div className="flex items-center justify-center h-64 text-admin_error">
      {error}
    </div>
  )

  const { totals, byType, byExtension } = data

  // Pie data: annotate with display label for the legend
  const sizeByType = byType.map((d) => ({ ...d, name: TYPE_LABELS[d.type] || d.type }))
  const countByType = byType.map((d) => ({ ...d, name: TYPE_LABELS[d.type] || d.type }))

  // Top 15 extensions for bar chart
  const topExtensions = byExtension.slice(0, 15)

  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.04) return null
    const RADIAN = Math.PI / 180
    const r = innerRadius + (outerRadius - innerRadius) * 0.6
    const x = cx + r * Math.cos(-midAngle * RADIAN)
    const y = cy + r * Math.sin(-midAngle * RADIAN)
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: 11, fontWeight: 600, pointerEvents: "none" }}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  return (
    <div className="p-6 space-y-6 bg-admin_canvas min-h-full overflow-y-auto">
      <Typography variant="admin_h3" className="my-0">Media Library Stats</Typography>

      {/* Pie charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Disk usage by type */}
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-5">
          <Typography variant="admin_h5" className="my-0 mb-4 text-admin_text">Disk Usage by Type</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={sizeByType}
                dataKey="totalSize"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                labelLine={false}
                label={renderPieLabel}
              >
                {sizeByType.map((entry) => (
                  <Cell key={entry.type} fill={TYPE_COLORS[entry.type] || TYPE_COLORS.other} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip mode="size" />} />
              <Legend
                formatter={(value) => (
                  <span style={{ fontSize: 12, color: "rgb(var(--color-admin_text))" }}>{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* File count by type */}
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-5">
          <Typography variant="admin_h5" className="my-0 mb-4 text-admin_text">File Count by Type</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={countByType}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                labelLine={false}
                label={renderPieLabel}
              >
                {countByType.map((entry) => (
                  <Cell key={entry.type} fill={TYPE_COLORS[entry.type] || TYPE_COLORS.other} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip mode="count" />} />
              <Legend
                formatter={(value) => (
                  <span style={{ fontSize: 12, color: "rgb(var(--color-admin_text))" }}>{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom row: summary list + extensions bar chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Summary card */}
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-5">
          <div className="flex flex-col divide-y divide-admin_bordered">
            {/* Totals row */}
            <div className="flex items-center justify-between py-2 first:pt-0">
              <span className="text-sm font-semibold text-admin_text">All Files</span>
              <div className="flex items-center gap-6">
                <span className="text-sm text-admin_text w-16 text-right">{totals.count.toLocaleString()}</span>
                <span className="text-sm text-admin_text_secondary w-20 text-right">{totals.formattedSize}</span>
              </div>
            </div>
            {/* Per-type rows */}
            {byType.map((t) => (
              <div key={t.type} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: TYPE_COLORS[t.type] || TYPE_COLORS.other }} />
                  <span className="text-sm text-admin_text">{TYPE_LABELS[t.type] || t.type}</span>
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-sm text-admin_text w-16 text-right">{t.count.toLocaleString()}</span>
                  <span className="text-sm text-admin_text_secondary w-20 text-right">{t.formattedSize}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Extensions bar chart */}
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-5">
          <Typography variant="admin_h5" className="my-0 mb-4 text-admin_text">
            Top Extensions by Disk Usage
          </Typography>
          <ResponsiveContainer width="100%" height={topExtensions.length * 36 + 40}>
            <BarChart
              data={topExtensions}
              layout="vertical"
              margin={{ top: 0, right: 80, left: 20, bottom: 0 }}
            >
              <CartesianGrid horizontal={false} stroke="rgb(var(--color-admin_bordered))" strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="totalSize"
                tickFormatter={(v) => formatSize(v)}
                tick={{ fontSize: 11, fill: "rgb(var(--color-admin_text_secondary))" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="ext"
                width={48}
                tick={{ fontSize: 12, fill: "rgb(var(--color-admin_text))" }}
                tickFormatter={(v) => `.${v}`}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="totalSize" radius={[0, 4, 4, 0]} minPointSize={2}>
                {topExtensions.map((entry) => {
                  const type = entry.ext === "unknown" ? "other" : (
                    {
                      jpg: "image", jpeg: "image", png: "image", gif: "image", webp: "image",
                      svg: "image", avif: "image", bmp: "image",
                      mp4: "video", webm: "video", mov: "video", avi: "video",
                      mp3: "audio", wav: "audio", ogg: "audio", aac: "audio", m4a: "audio",
                      pdf: "document", doc: "document", docx: "document",
                      xls: "document", xlsx: "document",
                    }[entry.ext] || "other"
                  )
                  return <Cell key={entry.ext} fill={TYPE_COLORS[type] || TYPE_COLORS.other} />
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  )
}
